import type {EntryPoint} from 'react-relay'
import type {LoaderFunctionArgs} from 'react-router'
import {getRequest} from 'relay-runtime'

import {PermissionQuery} from '../../hooks/usePermission'
import {getProjectPermissionsLocator} from '../../rest/permission'
import {
  isAgentDomainBreadcrumbsScreen,
  isAgentsScreen,
  isPipelineDomainBreadcrumbsScreen,
  isProjectDomainBreadcrumbsScreen,
  isQueueScreen,
  isScreenWithSidebar,
} from '../../routes'
import {Permission, ROOT_PROJECT_ID} from '../../types'
import type {ThinQueryParamsObject, ThinNestedEntryPointParamsObject} from '../../types/entryPoints'
import {makeResource} from '../../utils/makeResource'
import {AgentDomainBreadcrumbsEntryPoint} from '../common/AgentDomainBreadcrumbs/AgentDomainBreadcrumbs.entryPoint'
import {BuildDetailsContentEntryPoint} from '../common/Builds/BuildDetails/BuildDetailsContent/BuildDetailsContent.entryPoint'
import {OverviewBreadcrumbsEntryPoint} from '../common/OverviewBreadcrumbs/OverviewBreadcrumbs.entryPoint'
import {AgentsSidebarEntryPoint} from '../pages/AgentsPages/AgentsSidebar/AgentsSidebar.entryPoint'
import {getPermalinkLocatorInLoader} from '../pages/BuildPage/BuildPage.entryPoint'
import PipelinesBreadcrumbsEntryPoint from '../pages/PipelinesPages/components/PipelinesBreadcrumbs/PipelinesBreadcrumbs.entryPoint'

import type {AppNestedEntryPoints, AppOuterContainerComponent, AppQueries} from './App.container'
import {ProjectsSidebarEntryPoint} from './SidebarPanel/SidebarPanelContent/ProjectsSidebar/ProjectsSidebar.entryPoint'
import {QueueSidebarEntryPoint} from './SidebarPanel/SidebarPanelContent/QueueSidebar/QueueSidebar.entryPoint'

export const AppEntryPoint: EntryPoint<AppOuterContainerComponent, LoaderFunctionArgs> = {
  root: makeResource(
    'App',
    () => import(/* webpackChunkName: "App", webpackPrefetch: true */ './App.container'),
  ),
  getPreloadProps: args => {
    const {request} = args
    const queries: ThinQueryParamsObject<AppQueries> = {
      canCreateSubproject: {
        parameters: getRequest(PermissionQuery),
        variables: {
          locator: getProjectPermissionsLocator(Permission.CREATE_SUB_PROJECT, ROOT_PROJECT_ID),
          skip: false,
        },
        options: {networkCacheConfig: {metadata: {essential: true}}},
      },
    }
    const entryPoints: ThinNestedEntryPointParamsObject<AppNestedEntryPoints> = {}
    const url = new URL(request.url)
    const buildId = Number(url.hash)
    if (buildId) {
      entryPoints.buildDetailsContent = {
        entryPoint: BuildDetailsContentEntryPoint,
        entryPointParams: buildId,
      }
    }
    if (isScreenWithSidebar(url.pathname)) {
      if (isQueueScreen(url.pathname)) {
        entryPoints.queueSidebar = {entryPoint: QueueSidebarEntryPoint, entryPointParams: {}}
      } else if (isAgentsScreen(url.pathname)) {
        entryPoints.agentsSidebar = {entryPoint: AgentsSidebarEntryPoint, entryPointParams: {}}
      } else {
        entryPoints.projectsSidebar = {entryPoint: ProjectsSidebarEntryPoint, entryPointParams: {}}
      }
    }
    if (isPipelineDomainBreadcrumbsScreen(url.pathname)) {
      entryPoints.breadcrumbs = {
        entryPoint: PipelinesBreadcrumbsEntryPoint,
        entryPointParams: args,
      }
    } else if (isProjectDomainBreadcrumbsScreen(url.pathname)) {
      if (getPermalinkLocatorInLoader(args) == null) {
        entryPoints.breadcrumbs = {
          entryPoint: OverviewBreadcrumbsEntryPoint,
          entryPointParams: args,
        }
      }
    } else if (isAgentDomainBreadcrumbsScreen(url.pathname)) {
      entryPoints.breadcrumbs = {
        entryPoint: AgentDomainBreadcrumbsEntryPoint,
        entryPointParams: args,
      }
    }
    return {queries, entryPoints}
  },
}
